import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./GraphicDesign.css";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { Navigation, Pagination } from "swiper/modules";
import * as THREE from "three";

const categories = ["brand", "luxury", "festival", "travel"];

const GraphicDesign = () => {
  const [selectedCategory, setSelectedCategory] = useState("brand");
  const [showModal, setShowModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [isCarousel, setIsCarousel] = useState(false);

  const vantaRef = useRef(null);
  const vantaEffect = useRef(null);

  // ---------- AOS + VANTA ----------
  useEffect(() => {
    AOS.init();

    import("vanta/dist/vanta.halo.min").then((VANTA) => {
      if (!vantaEffect.current) {
        vantaEffect.current = VANTA.default({
          el: vantaRef.current,
          THREE: THREE,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
        });
      }
    });

    return () => {
      if (vantaEffect.current) vantaEffect.current.destroy();
    };
  }, []);

  // ---------- Image Data ----------
  const imageData = {
    brand: [
      {
        type: "single",
        images: ["/images/brand/b1.webp"],
      },
      {
        type: "carousel",
        images: [
          "/images/brand/b2.webp",
          "/images/brand/b3.webp",
          "/images/brand/b4.webp",
          "/images/brand/b5.webp",
          "/images/brand/b6.webp",
          "/images/brand/b7.webp",
          "/images/brand/b8.webp",
        ],
      },
    ],
    luxury: [
      {
        type: "carousel",
        images: ["/images/luxury/8.webp", "images/luxury/9.webp"],
      },
      {
        type: "carousel",
        images: [
          "/images/luxury/1.webp",
          "/images/luxury/2.webp",
          "/images/luxury/3.webp",
          "/images/luxury/4.webp",
          "/images/luxury/5.webp",
          "/images/luxury/6.webp",
          "/images/luxury/7.webp",
        ],
      },
      {
        type: "single",
        images: ["/images/luxury/10.webp"],
      },
    ],
    festival: [
      {
        type: "single",
        images: ["/images/festival/f1.webp"],
      },
    ],
    travel: [
      {
        type: "carousel",
        images: [
          "/images/travel/t1.webp",
          "/images/travel/t2.webp",
          "/images/travel/t3.webp",
          "/images/travel/t4.webp",
        ],
      },
      {
        type: "carousel",
        images: [
          "/images/travel/t5.webp",
          "/images/travel/t6.webp",
          "/images/travel/t7.webp",
          "/images/travel/t8.webp",
          "/images/travel/t9.webp",
          "/images/travel/t10.webp",
        ],
      },
      {
        type: "carousel",
        images: [
          "/images/travel/t11.webp",
          "/images/travel/t12.webp",
          "/images/travel/t13.webp",
          "/images/travel/t14.webp",
          "/images/travel/t15.webp",
          "/images/travel/t16.webp",
        ],
      },
    ],
  };

  // ---------- Handlers ----------
  const handleFilterChange = (category) => {
    setSelectedCategory(category);
  };

  const openModal = (images, isCarouselFlag) => {
    setModalImages(images);
    setIsCarousel(isCarouselFlag);
    setShowModal(true);
  };

  const closeModal = (e) => {
    if (e.target.classList.contains("modal")) {
      setShowModal(false);
      setModalImages([]);
      setIsCarousel(false);
    }
  };

  // ---------- Animation Variants ----------
  const heroVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1, transition: { duration: 1.2, ease: "easeOut" } },
  };

  const titleVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const fadeTransition = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.8 } },
    exit: { opacity: 0, transition: { duration: 0.4 } },
  };

  // ---------- Render ----------
  return (
    <div>
      {/* ===== HERO SECTION ===== */}
      <motion.div
        ref={vantaRef}
        className="vanta-hero"
        variants={heroVariants}
        initial="initial"
        animate="animate"
      >
        <motion.h1
          variants={titleVariants}
          initial="initial"
          animate="animate"
          className="hero-title"
        >
          Graphic Designs
        </motion.h1>
      </motion.div>


      {/* ===== CATEGORY FILTER BUTTONS ===== */}
      <div className="text-center mt-5">
        <LayoutGroup>
          {categories.map((cat) => (
            <motion.button
              key={cat}
              layout
              className={`category-btn ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => handleFilterChange(cat)}
              whileHover={{ scale: 1.1, backgroundColor: "#FF7043", color: "#fff" }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </motion.button>
          ))}
        </LayoutGroup>
      </div>

      {/* ===== GALLERY GRID ===== */}
      <div className="container mt-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            className="row"
            variants={fadeTransition}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {imageData[selectedCategory].map((item, index) => (
              <motion.div
                key={index}
                className="col-md-4 mb-4"
                data-aos="fade-up"
                data-aos-duration="1000"
                whileHover={{ scale: 1.02 }}
              >
                <motion.div
                  className="card gradient-card"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="card-body position-relative">
                    {item.type === "carousel" ? (
                      <>
                        <Swiper
                          modules={[Navigation]}
                          navigation={{
                            nextEl: `.next-btn-${index}`,
                            prevEl: `.prev-btn-${index}`,
                          }}
                          spaceBetween={10}
                          slidesPerView={1}
                          loop={true}
                        >
                          {item.images.map((img, i) => (
                            <SwiperSlide key={i}>
                              <LazyLoadImage
                                src={img}
                                alt={`Slide ${i}`}
                                className="img-fluid"
                                effect="blur"
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                        <div className={`slide-arrow prev-btn prev-btn-${index}`}>&#8249;</div>
                        <div className={`slide-arrow next-btn next-btn-${index}`}>&#8250;</div>
                      </>
                    ) : (
                      <LazyLoadImage
                        src={item.images[0]}
                        alt={`Image ${index}`}
                        className="img-fluid"
                        effect="blur"
                      />
                    )}

                    <div
                      className="zoom-btn"
                      onClick={() => openModal(item.images, item.type === "carousel")}
                    >
                      <MdFullscreen />
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ===== MODAL ===== */}
      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Image View</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  <span aria-hidden="true">
                    <MdFullscreenExit />  
                  </span>
                </button>
              </div>
              <div className="modal-body">
                {isCarousel ? (
                  <div className="swiper-container mt-3 position-relative">
                    <Swiper
                      modules={[Navigation, Pagination]}
                      spaceBetween={10}
                      slidesPerView={1}
                      loop={true}
                      navigation={{
                        nextEl: ".swiper-button-next-custom",
                        prevEl: ".swiper-button-prev-custom",
                      }}
                      pagination={{ clickable: true }}
                    >
                      {modalImages.map((image, index) => (
                        <SwiperSlide key={index}>
                          <img src={image} alt={`Modal ${index}`} className="modal-image" />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="swiper-button-prev-custom">&#8249;</div>
                    <div className="swiper-button-next-custom">&#8250;</div>
                  </div>
                ) : (
                  <motion.img
                    src={modalImages[0]}
                    alt="Large View"
                    className="modal-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphicDesign;
